import { CalendarCheck, MapPin, Clock, ArrowRight, RotateCcw } from "lucide-react";

const LOCS = { 1: "Carleton Place", 2: "Kanata", 3: "Ottawa" };

function goBook(p, slot) {
  try {
    localStorage.setItem("open_booking_prefill", JSON.stringify({ location_id: p.location_id, date: slot?.date, time: slot?.time }));
  } catch { /* noop */ }
  if (p.variation_id) {
    window.location.href = `/book?service=${encodeURIComponent(p.service_name || "")}&variation=${encodeURIComponent(p.variation_id)}&location=${p.location_id || 3}&src=lumi_book`;
    return;
  }
  const q = (p.service_name || "").replace(/\s*—.*$/, "").replace(/^\d+\.\s*/, "").trim();
  window.location.href = `/book?search=${encodeURIComponent(q)}&src=lumi_book`;
}

const PROVIDER = { karine: "Karine Lauzon RN", tanya: "Tanya (aesthetician)", linda: "Linda Lafrance NP" };

function InfoCard({ p, isFr }) {
  const title = (p.service_name || "").replace(/\s*—\s*\$\d[\s\S]*$/, "").replace(/\s*\$\d[\s\S]*$/, "").trim();
  return (
    <>
      <p className="font-serif text-lg sm:text-xl text-slate-900 mt-1 leading-snug" data-testid="lumi-code-title">
        <span className="inline-block mr-2 px-2 py-0.5 rounded-md bg-slate-900 text-amber-300 text-xs font-black tracking-wider align-middle">#{p.code}</span>
        <b>{title}</b>
        {p.price && <> · <b className="text-red-800">{p.price}</b> <span className="text-sm text-slate-600">+ tax</span></>}
      </p>
      <p className="text-sm text-slate-600 mt-1">{p.duration_minutes} min · {isFr ? "avec" : "with"} {PROVIDER[p.provider] || "our team"}{p.category ? ` · ${p.category}` : ""}</p>
      {p.description && <p className="text-sm text-slate-800 mt-2 leading-relaxed" data-testid="lumi-code-description">{p.description}</p>}
      {p.found ? (
        <>
          <p className="text-sm text-slate-700 mt-3">{isFr ? `Première place à ${p.location_name} : ` : `Earliest at ${p.location_name}: `}<b>{p.label}</b></p>
          <button type="button" onClick={() => goBook(p, p)} className="mt-2 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-3 text-sm font-bold shadow" data-testid="lumi-book-now">
            <Clock className="w-4 h-4" />{isFr ? `Réserver #${p.code} · ${p.label}` : `Book #${p.code} · ${p.label}`}<ArrowRight className="w-4 h-4" />
          </button>
        </>
      ) : (
        <button type="button" onClick={() => goBook(p, null)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-3 text-sm font-bold" data-testid="lumi-book-now">{isFr ? `Voir les dates pour #${p.code}` : `See dates for #${p.code}`}<ArrowRight className="w-4 h-4" /></button>
      )}
      <a href="/book/menu" className="block mt-2 text-xs text-emerald-800 underline" data-testid="lumi-open-menu">{isFr ? "Voir tout le menu par numéro →" : "See the whole menu by number →"}</a>
    </>
  );
}

export const LumiBookingProposal = ({ p, isFr, onReset, onPickLocation }) => {
  const needsLoc = p.needs?.includes("location");
  const needsSvc = p.needs?.includes("service");
  const svcShort = (p.service_name || "").replace(/^\d+\.\s*/, "").replace(/\s*—\s*\$\d+.*$/, "");
  return (
    <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4 sm:p-5 shadow-sm" data-testid="lumi-booking-proposal">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow">
          <CalendarCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">{isFr ? "Lumi peut réserver ça" : "Lumi can book that"}</p>
          {p.unknown_code ? (
            <p className="font-serif text-lg text-slate-900 mt-1" data-testid="lumi-code-unknown">{isFr ? `Je ne trouve aucun soin portant le numéro #${p.code}. Vérifiez le numéro sur le menu ou dites-moi le nom du soin.` : `I can't find a treatment numbered #${p.code}. Double-check the number on the menu, or tell me the treatment name.`}</p>
          ) : p.not_at_location ? (
            <p className="font-serif text-lg text-slate-900 mt-1">{isFr ? `#${p.code} ${p.service_name} n'est pas offert à ${p.location_name}. Essayez une autre clinique.` : `#${p.code} ${p.service_name} isn't offered at ${p.location_name} — try another clinic.`}</p>
          ) : p.mode === "info" ? (
            <InfoCard p={p} isFr={isFr} />
          ) : needsSvc ? (
            <p className="font-serif text-lg text-slate-900 mt-1">{isFr ? "Quel soin aimeriez-vous réserver ? Dites-moi le nom (ex. « Makeover 9 », « Botox », « HydraFacial »)." : "Which treatment would you like? Tell me the name (e.g. “Makeover 9”, “Botox”, “HydraFacial”)."}</p>
          ) : needsLoc ? (
            <>
              <p className="font-serif text-lg text-slate-900 mt-1">{isFr ? `${svcShort} — à quelle clinique ?` : `${svcShort} — which clinic?`}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(LOCS).map(([id, name]) => (
                  <button key={id} type="button" onClick={() => onPickLocation(id)} className="px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-900 text-sm font-semibold hover:bg-emerald-100" data-testid={`lumi-pick-loc-${id}`}>
                    <MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{name}
                  </button>
                ))}
              </div>
            </>
          ) : !p.found && !p.requested_date ? (
            <InfoCard p={p} isFr={isFr} />
          ) : !p.found ? (
            <p className="font-serif text-lg text-slate-900 mt-1">{isFr ? `Aucune place pour ${svcShort} à ${p.location_name} dans les 3 prochaines semaines — appelez-nous au (613) 850-0805.` : `No openings for ${svcShort} at ${p.location_name} in the next 3 weeks — text us at (613) 850-0805 and we'll squeeze you in.`}</p>
          ) : (
            <>
              <p className="font-serif text-lg sm:text-xl text-slate-900 mt-1 leading-snug">
                <b>{svcShort}</b> {isFr ? "à" : "at"} <b>{p.location_name}</b>
                {p.exact_day
                  ? (isFr ? " — la première place est " : " — earliest is ")
                  : (isFr ? " — rien ce jour-là ; la prochaine place est " : " — nothing that day; the next opening is ")}
                <b>{p.label}</b>.
              </p>
              <button type="button" onClick={() => goBook(p, p)} className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-3 text-sm font-bold shadow" data-testid="lumi-book-now">
                <Clock className="w-4 h-4" />{isFr ? `Réserver ${p.label}` : `Book ${p.label}`}<ArrowRight className="w-4 h-4" />
              </button>
              {p.alternatives?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {p.alternatives.map((a) => (
                    <button key={a.time + a.date} type="button" onClick={() => goBook(p, a)} className="px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-900 text-xs font-semibold hover:bg-emerald-100" data-testid={`lumi-book-alt-${a.time}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-2">{isFr ? "Un tap — vos coordonnées sont pré-remplies si vous êtes déjà cliente." : "One tap — your details are pre-filled if you've visited before."}</p>
            </>
          )}
          <button type="button" onClick={onReset} className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="lumi-booking-reset">
            <RotateCcw className="w-3 h-3" />{isFr ? "Autre question" : "Ask something else"}
          </button>
        </div>
      </div>
    </div>
  );
};
